// optionRowFilters — config-driven, CLIENT-SIDE narrowing of the RAW rows a
// lookup dropdown returned, shared by AddFormV1 and EditFormV1.
//
// It runs on the raw `/admin/lookup-dropdown-values` rows ({label, value,
// extraData}) BEFORE they are normalized into antd options, because every
// decision here is made from `extraData` — the values the backend was asked to
// carry alongside each row via `extraFields`.
//
// ── Why client-side at all ───────────────────────────────────────────────────
// The proper mechanism is `field.lookupFilters` (multi-condition, evaluated
// server-side). It IS configured on the Jobs "Assign To" field, but the
// currently DEPLOYED backend predates that key and drops it while reading the
// config, so the dropdown falls back to "everybody". `extraFields` however IS
// supported by that build (the employer→recruiter `optionsRequireExtraField`
// feature uses it live), so the same restriction can be expressed as data on
// each row and applied in the browser until the backend ships.
//
// BOTH filters will be active once the backend ships. They are deliberately
// written to express the SAME rule, so the result is identical rather than
// contradictory:
//
//   server lookupFilters                         client keys (this module)
//   ------------------------------------------   -----------------------------
//   roleId in (roles where roleName in [ROLES])   optionsRequireExtraField:
//                                                   "roleId"
//                                                 optionsRequireExtraValue:
//                                                   "<those roleIds>"
//   legacyUserId in (users whose reportingId      optionsHierarchyParentField:
//     chains up to currentUserId, recursive)        "reportingId"
//                                                 optionsHierarchySelfFrom:
//                                                   "currentUserId"
//
// Both narrow to the same set, and an AND of a set with itself is that set —
// so the dropdown shows the same rows whether one or both are in force. The
// seeder (cmd/wire-jobs-assignto-lookup) writes both sides from ONE role list
// so they can never drift apart in config either.
//
// ── Config keys (all must exist on the Go FormField struct or the admin save
//    API silently drops them) ─────────────────────────────────────────────────
//   optionsRequireExtraField    — extraData key to test (e.g. "roleId")
//   optionsRequireExtraValue    — NEW. Allowed value(s), single or
//                                 comma-separated. ABSENT ⇒ today's exact
//                                 `=== true` semantics, unchanged.
//   optionsHierarchyParentField — NEW. extraData key holding each row's PARENT
//                                 id in the same id space as the row's value
//                                 (e.g. "reportingId").
//   optionsHierarchySelfFrom    — NEW. Identity token naming whose downline to
//                                 keep (e.g. "currentUserId").
//
// Nothing here knows about jobs, assignedTo, recruiters, roleId or reportingId:
// every name above arrives as config.

// Depth cap for the parent walk. Well beyond any real org chart, and the
// visited-set below already stops cycles — this is a second, unconditional
// backstop so a malformed chain can never spin.
const MAX_HIERARCHY_DEPTH = 64;

// Identity tokens resolvable in the BROWSER. The server-side lookupFilters
// resolve `currentUserId` from the authenticated request; client-side the same
// value comes from localStorage `userId` — the identical source
// ZINNEXT-V2's jobConfig.js / useVisibleTabs use for "is this me?".
// An unknown token is NOT silently treated as "no filter applied without
// saying so": resolveIdentity returns '' and the caller warns and skips.
export function resolveIdentity(token) {
  const key = String(token ?? '').trim();
  if (key !== 'currentUserId') return '';
  try {
    if (typeof localStorage === 'undefined') return '';
    return String(localStorage.getItem('userId') ?? '').trim();
  } catch {
    return '';
  }
}

const asKey = (v) => String(v ?? '').trim().toLowerCase();
const filled = (v) => v !== undefined && v !== null && String(v).trim() !== '';

/** The allowed-value set from a comma-separated (or array) config value. */
function allowedValueKeys(raw) {
  const list = Array.isArray(raw) ? raw : String(raw ?? '').split(',');
  return new Set(list.map(asKey).filter((v) => v !== ''));
}

/**
 * extraDataArrived — did the backend actually send this key?
 *
 * TRUE when at least one row carries a non-empty value at `key`. This is the
 * fail-safe pivot (see filterLookupOptionRows): a deployed build that ignores
 * `extraFields` returns rows with no extraData at all, and filtering on data
 * that never arrived would empty the dropdown for a reason no admin can see.
 * "Present but nothing matches" is a legitimate empty and IS honoured.
 */
export function extraDataArrived(rows, key) {
  if (!key || !Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((row) => filled(row?.extraData?.[key]));
}

/**
 * matchesRequiredExtra — the value test for ONE row.
 *
 * With no `optionsRequireExtraValue` this is byte-for-byte the old behaviour:
 * strictly `=== true` (used live by "only list a VERIFIED recruiter").
 * With one, it is a LOOSE string comparison so a stored numeric 10 matches a
 * configured "10" — the lookup API may hand back either, depending on whether
 * the value survived an aggregation projection as a number or a string.
 */
export function matchesRequiredExtra(row, key, allowedRaw) {
  const actual = row?.extraData?.[key];
  if (!filled(allowedRaw)) return actual === true;
  const allowed = allowedValueKeys(allowedRaw);
  if (allowed.size === 0) return actual === true;
  return allowed.has(asKey(actual));
}

/**
 * buildParentMap — { rowValueKey: parentValueKey } from the returned rows.
 *
 * The map is built from the ROWS THEMSELVES, so a transitive chain can only be
 * proven through people who are in the returned page of results. That is the
 * intended semantics here: the option list is what we are filtering.
 */
export function buildParentMap(rows, parentField) {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const self = asKey(row?.value);
    if (!self) return;
    map.set(self, asKey(row?.extraData?.[parentField]));
  });
  return map;
}

/**
 * isInDownline — does walking parent links from `startValue` reach `selfId`?
 *
 * CYCLE GUARD: two independent stops.
 *   1. `seen` — a node revisited means the chain looped (A→B→A, or a
 *      self-referencing reportingId pointing at its own row); return false.
 *   2. MAX_HIERARCHY_DEPTH — an unconditional iteration cap, so even a map
 *      mutated mid-walk or an unforeseen shape cannot spin the browser.
 *
 * SELF-INCLUSION — DECIDED: the signed-in user does NOT appear in their own
 * list. The walk starts at the row's PARENT, so a row whose value equals
 * selfId only survives if it also reports (transitively) to itself, which the
 * cycle guard rejects. Rationale: the configured rule is "users who report to
 * me", and I do not report to myself; a manager assigning work picks from
 * their team. It also keeps this filter identical to the server-side
 * `lookupFilters` condition (`reportingId` chains up from currentUserId),
 * which likewise never yields the signed-in user's own row — the two filters
 * must agree exactly or the union/intersection of the two would differ by one
 * row depending on which backend is deployed.
 */
export function isInDownline(startValue, parentMap, selfId) {
  const self = asKey(selfId);
  if (!self) return false;
  const seen = new Set();
  let current = asKey(startValue);
  if (!current) return false;
  seen.add(current);
  for (let depth = 0; depth < MAX_HIERARCHY_DEPTH; depth += 1) {
    const parent = parentMap.get(current);
    if (!parent) return false;
    if (parent === self) return true;
    if (seen.has(parent)) return false; // cycle
    seen.add(parent);
    current = parent;
  }
  return false;
}

/**
 * lookupExtraFieldKeys — every extraData key the request must ask for.
 *
 * The caller passes this to the `extraFields` query param instead of
 * `field.extraFields` alone, so configuring a filter key is enough: the admin
 * cannot forget to also list it under extraFields and get a silently empty (or
 * silently unfiltered) dropdown. Explicit `field.extraFields` entries (used by
 * autofillFrom) are preserved and come first; order is stable and de-duped.
 */
export function lookupExtraFieldKeys(field = {}) {
  const keys = [];
  const push = (k) => {
    const key = String(k ?? '').trim();
    if (key && !keys.includes(key)) keys.push(key);
  };
  (Array.isArray(field.extraFields) ? field.extraFields : []).forEach(push);
  push(field.optionsRequireExtraField);
  push(field.optionsHierarchyParentField);
  return keys;
}

/** Does this field configure any row filter at all? */
export function hasOptionRowFilters(field = {}) {
  return Boolean(field.optionsRequireExtraField || field.optionsHierarchyParentField);
}

/**
 * filterLookupOptionRows — the whole client-side restriction, in order:
 *   A. value match on one extraData key
 *   B. keep only the signed-in user's downline
 * Both are optional and independent; configuring neither returns `rows` as-is.
 *
 * FAIL-SAFE (deliberate asymmetry, see the requirement it was built for):
 *   • data NEVER ARRIVED (no row carries the configured key, or the identity
 *     token cannot be resolved) → SKIP that filter and log ONE warning naming
 *     the field. An admin must be able to tell "nobody reports to me"
 *     (legitimate empty) from "the deployed build ignored extraFields"
 *     (broken), and an unexplained empty dropdown hides a mandatory field
 *     behind a data problem the user cannot see or fix.
 *   • data ARRIVED and simply nothing matches → return the empty list
 *     faithfully. Failing open there would re-offer exactly the rows the admin
 *     declared off-limits, i.e. reintroduce the bug.
 *
 * @param {Array}  rows   raw lookup rows ({label, value, extraData})
 * @param {object} field  the field config
 * @param {object} opts   { source: 'AddFormV1' | 'EditFormV1' } for the warning
 * @returns {Array} the rows to keep
 */
export function filterLookupOptionRows(rows, field = {}, opts = {}) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const where = opts.source ? `[${opts.source}]` : '[optionRowFilters]';
  const name = field.field ?? field.label ?? '(unnamed field)';
  const warn = typeof opts.warn === 'function'
    ? opts.warn
    : (msg) => { if (typeof console !== 'undefined') console.warn(msg); };
  let out = rows;

  // A — value match.
  const valueKey = String(field.optionsRequireExtraField ?? '').trim();
  if (valueKey) {
    if (!extraDataArrived(out, valueKey)) {
      warn(`${where} "${name}": option filter SKIPPED — no option carried extraData["${valueKey}"], `
        + 'so the value filter could not be applied (the lookup API returned no such extra field). '
        + 'Showing the unfiltered list; this is NOT "nothing matched".');
    } else {
      out = out.filter((row) => matchesRequiredExtra(row, valueKey, field.optionsRequireExtraValue));
    }
  }

  // B — hierarchy (downline of the signed-in user).
  const parentField = String(field.optionsHierarchyParentField ?? '').trim();
  if (parentField) {
    const token = String(field.optionsHierarchySelfFrom ?? '').trim() || 'currentUserId';
    const selfId = resolveIdentity(token);
    if (!selfId) {
      warn(`${where} "${name}": hierarchy filter SKIPPED — identity "${token}" could not be resolved `
        + '(no signed-in user id available). Showing the list unrestricted by reporting line.');
    } else if (!extraDataArrived(rows, parentField)) {
      warn(`${where} "${name}": hierarchy filter SKIPPED — no option carried extraData["${parentField}"], `
        + 'so the reporting chain could not be walked (the lookup API returned no such extra field). '
        + 'Showing the list unrestricted by reporting line; this is NOT "nobody reports to you".');
    } else {
      // The chain is walked over ALL returned rows, not the value-filtered
      // ones: an intermediate manager may hold a role the value filter
      // excludes (a recruiter reporting to a LEAD RECRUITER reporting to me),
      // and dropping that link first would sever a chain that genuinely
      // reaches me. This also matches the server-side condition, which
      // evaluates the two conditions independently over the whole collection.
      const parentMap = buildParentMap(rows, parentField);
      out = out.filter((row) => isInDownline(row?.value, parentMap, selfId));
    }
  }

  return out;
}

export default {
  filterLookupOptionRows,
  lookupExtraFieldKeys,
  hasOptionRowFilters,
  matchesRequiredExtra,
  extraDataArrived,
  buildParentMap,
  isInDownline,
  resolveIdentity,
};
